import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Phone, Calendar, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  subject: string;
  message: string;
  area_of_inquiry: string;
  status: string;
  created_at: string;
  region_id?: string | null;
}

interface Region {
  id: string;
  name: string;
}

export const MessagesManager: React.FC<{ userRole: string; userRegion?: string }> = ({ userRole, userRegion }) => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [filterRegion, setFilterRegion] = useState<string>('all');
  const [filterAreaOfInquiry, setFilterAreaOfInquiry] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const isGlobalAdmin = ['global_admin', 'president', 'Global Admin', 'President'].includes(userRole);
  const isRegionalAdmin = ['regional_admin', 'Regional Admin'].includes(userRole);

  useEffect(() => {
    fetchMessages();
    if (isGlobalAdmin) {
      fetchRegions();
    }
  }, [userRole, userRegion, filterRegion, filterAreaOfInquiry, filterStatus]);

  const fetchRegions = async () => {
    const { data } = await supabase.from('regions').select('id, name').order('name');
    if (data) {
      setRegions(data);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (isRegionalAdmin && userRegion) {
        query = query.eq('region_id', userRegion);
      } else if (isGlobalAdmin && filterRegion !== 'all') {
        query = query.eq('region_id', filterRegion);
      }

      // Apply area of inquiry filter
      if (filterAreaOfInquiry !== 'all') {
        query = query.eq('area_of_inquiry', filterAreaOfInquiry);
      }

      // Apply status filter
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      setMessages(data || []);
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMessageStatus = async (messageId: string, newStatus: string) => {
    const { error } = await supabase
      .from('contact_messages')
      .update({ status: newStatus })
      .eq('id', messageId);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    } else {
      fetchMessages();
      toast({
        title: "Success",
        description: "Message status updated"
      });
    }
  };

  const handleReply = (message: ContactMessage) => {
    setSelectedMessage(message);
    setReplyText(`Dear ${message.name},\n\nThank you for contacting OFUESI.\n\n`);
    setReplyDialogOpen(true);
  };

  const sendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    try {
      setSendingReply(true);

      const { error } = await supabase.functions.invoke('send-message-reply', {
        body: {
          message_id: selectedMessage.id,
          subject: `Re: ${selectedMessage.subject}`,
          reply_message: replyText,
        }
      });

      if (error) throw error;

      // Update message status to 'resolved'
      await updateMessageStatus(selectedMessage.id, 'resolved');

      toast({
        title: "Success",
        description: "Reply sent successfully"
      });

      setReplyDialogOpen(false);
      setReplyText('');
      setSelectedMessage(null);
    } catch (error: any) {
      console.error('Error sending reply:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send reply",
        variant: "destructive"
      });
    } finally {
      setSendingReply(false);
    }
  };

  const getRegionName = (regionId: string | null | undefined) => {
    if (!regionId) return 'International';
    const region = regions.find(r => r.id === regionId);
    return region?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold">Contact Messages</h2>
          <p className="text-muted-foreground">View and respond to contact form submissions</p>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap gap-3">
          {isGlobalAdmin && (
            <Select value={filterRegion} onValueChange={setFilterRegion}>
              <SelectTrigger className="w-48 bg-background">
                <SelectValue placeholder="Filter by region" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region.id} value={region.id}>{region.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={filterAreaOfInquiry} onValueChange={setFilterAreaOfInquiry}>
            <SelectTrigger className="w-48 bg-background">
              <SelectValue placeholder="Area of Inquiry" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Areas</SelectItem>
              <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              <SelectItem value="Prayer Request">Prayer Request</SelectItem>
              <SelectItem value="Ministry Partnership">Ministry Partnership</SelectItem>
              <SelectItem value="Event Information">Event Information</SelectItem>
              <SelectItem value="New Student">New Student</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {messages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No messages found
            </CardContent>
          </Card>
        ) : (
          messages.map((message) => (
            <Card key={message.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{message.name}</h3>
                        <Badge variant={
                          message.status === 'new' ? 'default' :
                          message.status === 'in_progress' ? 'secondary' :
                          'outline'
                        }>
                          {message.status === 'new' ? 'New' :
                           message.status === 'in_progress' ? 'In Progress' :
                           'Resolved'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {message.area_of_inquiry}
                        </Badge>
                        {isGlobalAdmin && (
                          <Badge variant="secondary" className="text-xs">
                            {getRegionName(message.region_id)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <a href={`mailto:${message.email}`} className="hover:underline flex items-center gap-1 hover:text-primary">
                          <Mail className="h-3 w-3" />
                          {message.email}
                        </a>
                        {message.phone && (
                          <a href={`tel:${message.phone}`} className="hover:underline flex items-center gap-1 hover:text-primary">
                            <Phone className="h-3 w-3" />
                            {message.phone}
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(message.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={message.status}
                        onValueChange={(newStatus) => updateMessageStatus(message.id, newStatus)}
                      >
                        <SelectTrigger className="w-36 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleReply(message)}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Reply
                      </Button>
                    </div>
                  </div>

                  {message.subject && (
                    <div className="bg-muted/50 p-3 rounded-md">
                      <span className="text-sm font-medium">Subject: </span>
                      <span className="text-sm">{message.subject}</span>
                    </div>
                  )}

                  <div className="bg-muted/30 p-4 rounded-md">
                    <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reply to {selectedMessage?.name}</DialogTitle>
            <DialogDescription>
              Send a reply to {selectedMessage?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-md">
              <p className="text-sm font-medium mb-2">Original Message:</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedMessage?.message}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Reply</label>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
                rows={8}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={sendReply} disabled={sendingReply || !replyText.trim()}>
              <Send className="h-4 w-4 mr-2" />
              {sendingReply ? 'Sending...' : 'Send Reply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
